import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useActivitiesStore, SavedActivity } from '../store/activitiesStore';
import { documentsAPI } from '../services/api';
import apiClient from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TrashIcon } from '@heroicons/react/24/outline';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { marked } from 'marked';

// Helper function to generate UUIDs with fallback for browsers that don't support crypto.randomUUID
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface WillField {
  label: string;
  value: string;
  placeholder?: string;
  type?: 'text' | 'date';
  displayValue?: string; // Formatted display value for dates
}

interface FieldGroup {
  groupName: string;
  fields: {
    label: string;
    type: 'text' | 'date';
  }[];
}

interface GroupInstance {
  [fieldLabel: string]: string | { value: string; displayValue?: string };
}

// Helper function to format dates based on country
const formatDateForCountry = (dateString: string, country: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  // Format based on country conventions
  switch (country) {
    case 'United States':
      return `${month}/${day}/${year}`; // MM/DD/YYYY

    case 'Canada':
      return `${year}-${month}-${day}`; // YYYY-MM-DD (ISO format)

    case 'United Kingdom':
    case 'Australia':
    case 'New Zealand':
    case 'Ireland':
    case 'South Africa':
    case 'India':
    case 'Singapore':
    case 'Hong Kong':
    case 'Malaysia':
      return `${day}/${month}/${year}`; // DD/MM/YYYY

    case 'Germany':
    case 'Austria':
    case 'Switzerland':
      return `${day}.${month}.${year}`; // DD.MM.YYYY

    case 'France':
    case 'Italy':
    case 'Spain':
    case 'Portugal':
    case 'Belgium':
    case 'Netherlands':
    case 'Greece':
    case 'Poland':
    case 'Czech Republic':
    case 'Hungary':
    case 'Romania':
    case 'Bulgaria':
    case 'Croatia':
    case 'Slovenia':
    case 'Slovakia':
    case 'Lithuania':
    case 'Latvia':
    case 'Estonia':
    case 'Luxembourg':
    case 'Malta':
    case 'Cyprus':
    case 'Iceland':
    case 'Sweden':
    case 'Norway':
    case 'Denmark':
    case 'Finland':
      return `${day}/${month}/${year}`; // DD/MM/YYYY

    case 'Japan':
    case 'China':
    case 'South Korea':
    case 'Taiwan':
      return `${year}/${month}/${day}`; // YYYY/MM/DD

    case 'Brazil':
    case 'Mexico':
    case 'Argentina':
    case 'Chile':
    case 'Colombia':
    case 'Peru':
      return `${day}/${month}/${year}`; // DD/MM/YYYY

    case 'Thailand':
    case 'Indonesia':
    case 'Philippines':
    case 'Vietnam':
      return `${day}/${month}/${year}`; // DD/MM/YYYY

    case 'Israel':
      return `${day}.${month}.${year}`; // DD.MM.YYYY

    case 'United Arab Emirates':
    case 'Saudi Arabia':
      return `${day}/${month}/${year}`; // DD/MM/YYYY

    case 'Turkey':
    case 'Russia':
    case 'Ukraine':
      return `${day}.${month}.${year}`; // DD.MM.YYYY

    default:
      return `${day}/${month}/${year}`; // Default to DD/MM/YYYY
  }
}

// Helper function to consolidate field names
// Removes fields with slashes (descriptions/examples) and normalizes similar fields
const consolidateFields = (fields: string[]): string[] => {
  const fieldMap = new Map<string, string>(); // Maps normalized key to best field name
  const groupKeywords = ['beneficiary', 'beneficiaries', 'executor', 'executors', 'witness', 'witnesses', 'guardian', 'guardians', 'seller', 'buyer'];

  fields.forEach(field => {
    const lowerField = field.toLowerCase();

    // Skip fields that contain slashes (these are descriptions/examples like "spouse/children/other beneficiaries")
    if (field.includes('/')) {
      return;
    }

    // Check if this field matches a group keyword
    let normalizedKey = field;
    let bestFieldName = field;

    for (const keyword of groupKeywords) {
      if (lowerField.includes(keyword)) {
        // Normalize to singular form
        // Map plural keywords to their singular counterparts
        const singularForm = keyword === 'beneficiaries'
          ? 'beneficiary'
          : keyword === 'executors'
          ? 'executor'
          : keyword === 'witnesses'
          ? 'witness'
          : keyword === 'guardians'
          ? 'guardian'
          : keyword; // Already singular (beneficiary, executor, witness, guardian, seller, buyer)

        normalizedKey = singularForm;

        // Prefer cleaner field names (e.g., "beneficiary" over "Specific bequests, e.g., I give my real property located at [address] to [beneficiary]")
        // Prefer shorter, simpler names
        if (field.toLowerCase() === keyword || field.toLowerCase() === singularForm) {
          bestFieldName = keyword.charAt(0).toUpperCase() + singularForm.slice(1);
        } else if (field.toLowerCase().startsWith('name of')) {
          // "Name of Executor" is cleaner than just "Executor" in some contexts
          bestFieldName = field;
        } else {
          // Keep the field as-is for now
          bestFieldName = field;
        }
        break;
      }
    }

    // If we already have this normalized key, keep the shorter/cleaner version
    if (fieldMap.has(normalizedKey)) {
      const existing = fieldMap.get(normalizedKey)!;
      // Prefer shorter, simpler field names
      if (bestFieldName.length < existing.length || bestFieldName.toLowerCase().startsWith('name of')) {
        fieldMap.set(normalizedKey, bestFieldName);
      }
    } else {
      fieldMap.set(normalizedKey, bestFieldName);
    }
  });

  return Array.from(fieldMap.values());
};

export const GenerateWill: React.FC = () => {
  const location = useLocation();
  const [activityName, setActivityName] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [loading, setLoading] = useState(false);
  const [willFields, setWillFields] = useState<WillField[]>([]);
  const [showWillFields, setShowWillFields] = useState(false);
  const [willTemplate, setWillTemplate] = useState('');
  const [generatedWill, setGeneratedWill] = useState('');
  const [showGeneratedWill, setShowGeneratedWill] = useState(false);
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>([]);
  const [groupInstances, setGroupInstances] = useState<Record<string, GroupInstance[]>>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Template data from API
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [noTemplatesAvailable, setNoTemplatesAvailable] = useState(false);

  // Activity management from store
  const { addActivity, updateActivity, currentActivityId, setCurrentActivityId, getActivityById } = useActivitiesStore();
  const [lastSavedActivityName, setLastSavedActivityName] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch available countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiClient.get('/wills/countries');
        setAvailableCountries(response.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountries();
  }, []);

  // Fetch available states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (!country) {
        setAvailableStates([]);
        return;
      }

      try {
        const response = await apiClient.get(`/wills/countries/${encodeURIComponent(country)}/states`);
        setAvailableStates(response.data);
      } catch (error) {
        console.error('Error fetching states:', error);
        setAvailableStates([]);
      }
    };
    fetchStates();
  }, [country]);

  // Clear current activity and reset form when navigating without a specific activity to load
  // or when explicitly requested via clearActivity flag
  useEffect(() => {
    const state = location.state as { loadActivityId?: string; clearActivity?: boolean } | null;
    if (!state?.loadActivityId || state?.clearActivity) {
      // Reset all form state for a fresh activity
      setCurrentActivityId(null);
      setActivityName('');
      setCountry('');
      setState('');
      setMunicipality('');
      setWillFields([]);
      setShowWillFields(false);
      setGeneratedWill('');
      setShowGeneratedWill(false);
      setWillTemplate('');
      setFieldGroups([]);
      setGroupInstances({});
      setLastSavedActivityName('');
      setIsSaved(false);
      setHasUnsavedChanges(false);
    }
  }, [location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load activity when navigating with loadActivityId
  useEffect(() => {
    const state = location.state as { loadActivityId?: string } | null;
    if (state?.loadActivityId) {
      const activity = getActivityById(state.loadActivityId);
      if (activity && activity.activityType === 'will-generation' && activity.willData) {
        // Restore all will generation state
        setCountry(activity.willData.country);
        setState(activity.willData.state);
        setMunicipality(activity.willData.municipality);
        setWillTemplate(activity.willData.willTemplate);
        setGeneratedWill(activity.willData.generatedWill);
        setShowGeneratedWill(true);

        // Restore willFields from the stored object format
        const restoredFields = Object.entries(activity.willData.willFields).map(([label, value]) => {
          // Check if value is a date object with displayValue
          if (typeof value === 'object' && value !== null && 'displayValue' in value) {
            return {
              label,
              type: 'date' as const,
              value: (value as any).value,
              displayValue: (value as any).displayValue
            };
          }
          return {
            label,
            type: 'text' as const,
            value: value as string
          };
        });
        setWillFields(restoredFields);
        setShowWillFields(true);

        // Restore field groups
        const restoredGroups = activity.willData.fieldGroups.map(groupName => ({
          groupName,
          fields: [{ label: 'Name', type: 'text' as const }]
        }));
        setFieldGroups(restoredGroups);

        // Restore group instances
        setGroupInstances(activity.willData.groupInstances);

        setActivityName(activity.name);
        setLastSavedActivityName(activity.name);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }
    }
  }, [location.state, getActivityById]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear state when country changes
  useEffect(() => {
    setState('');
    setNoTemplatesAvailable(false);
  }, [country]);

  // Check template availability when state changes
  useEffect(() => {
    const checkTemplateAvailability = async () => {
      if (!country || !state) {
        setNoTemplatesAvailable(false);
        return;
      }

      try {
        // Try to fetch the template to check if it exists
        const stateOrCountry = state || country;
        await apiClient.get(`/wills/template/${encodeURIComponent(country)}/${encodeURIComponent(stateOrCountry)}`);
        setNoTemplatesAvailable(false);
      } catch (error: any) {
        if (error.response?.status === 404) {
          setNoTemplatesAvailable(true);
        } else {
          setNoTemplatesAvailable(false);
        }
      }
    };
    checkTemplateAvailability();
  }, [country, state]);

  // Helper function to analyze template for repeatable groups based on structure, not just field labels
  const analyzeTemplateStructure = (template: string): FieldGroup[] => {
    const groups: FieldGroup[] = [];
    const groupKeywords = ['beneficiary', 'beneficiaries', 'executor', 'executors', 'witness', 'witnesses', 'guardian', 'guardians'];

    // Look for sections in the template that mention these keywords
    groupKeywords.forEach(keyword => {
      // Map plural keywords to their singular counterparts
      const singularForm = keyword === 'beneficiaries'
        ? 'beneficiary'
        : keyword === 'executors'
        ? 'executor'
        : keyword === 'witnesses'
        ? 'witness'
        : keyword === 'guardians'
        ? 'guardian'
        : keyword; // Already singular (beneficiary, executor, witness, guardian)

      const capitalizedName = singularForm.charAt(0).toUpperCase() + singularForm.slice(1);

      // Check if this keyword appears in the template
      const keywordPattern = new RegExp(`\\b${keyword}\\b`, 'i');
      if (keywordPattern.test(template)) {
        // This group exists in the template, add it with a generic "Name" field
        groups.push({
          groupName: capitalizedName,
          fields: [{ label: 'Name', type: 'text' }]
        });
      }
    });

    return groups;
  };

  // Helper function to detect if fields belong to a repeatable group
  const detectFieldGroups = (fieldLabels: string[], template: string): { groups: FieldGroup[]; regularFields: string[] } => {
    // First, analyze the template structure to see what groups actually exist
    const structuralGroups = analyzeTemplateStructure(template);

    const groupKeywords = ['beneficiary', 'beneficiaries', 'executor', 'executors', 'witness', 'witnesses', 'guardian', 'guardians'];
    const regularFields: string[] = [];
    const groupedFieldLabels = new Set<string>();

    // Find fields that match group patterns and enhance the structural groups
    const groupMap: Record<string, Map<string, 'text' | 'date'>> = {};

    // Initialize groupMap with structural groups
    structuralGroups.forEach(group => {
      groupMap[group.groupName] = new Map();
      group.fields.forEach(field => {
        groupMap[group.groupName].set(field.label, field.type);
      });
    });

    fieldLabels.forEach(label => {
      const lowerLabel = label.toLowerCase();
      let foundGroup = false;

      for (const keyword of groupKeywords) {
        if (lowerLabel.includes(keyword)) {
          // Extract the base group name (singular form)
          // Map plural keywords to their singular counterparts
          const groupName = keyword === 'beneficiaries'
            ? 'beneficiary'
            : keyword === 'executors'
            ? 'executor'
            : keyword === 'witnesses'
            ? 'witness'
            : keyword === 'guardians'
            ? 'guardian'
            : keyword; // Already singular (beneficiary, executor, witness, guardian)

          const capitalizedName = groupName.charAt(0).toUpperCase() + groupName.slice(1);

          if (!groupMap[capitalizedName]) {
            groupMap[capitalizedName] = new Map();
          }

          // Determine field type
          const isDateField = /\bdate\b/i.test(label) && !/\bdates\b/i.test(label);
          const fieldType: 'text' | 'date' = isDateField ? 'date' : 'text';

          // Extract the field attribute (e.g., "name", "address", "relationship")
          let fieldAttribute = label
            .replace(new RegExp(keyword, 'gi'), '')
            .replace(/['"]s?\s*/gi, '') // Remove possessives
            .trim();

          // Remove numbered patterns like "of 1", "of 2", "1", "2", etc.
          fieldAttribute = fieldAttribute
            .replace(/\s+of\s+\d+/gi, '') // Remove "of 1", "of 2", etc.
            .replace(/\s+\d+$/gi, '') // Remove trailing numbers like " 1", " 2"
            .replace(/^\d+\s+/gi, '') // Remove leading numbers like "1 ", "2 "
            .trim();

          // Normalize common patterns
          // "Name of" → "Name", "Address of" → "Address", etc.
          fieldAttribute = fieldAttribute.replace(/\s+of\s*$/gi, '').trim();

          // If field attribute is empty or too generic, try to infer from the label
          if (!fieldAttribute || fieldAttribute.length < 2) {
            fieldAttribute = 'Name';
          }

          // Use Map to automatically deduplicate field attributes
          groupMap[capitalizedName].set(fieldAttribute, fieldType);

          groupedFieldLabels.add(label);
          foundGroup = true;
          break;
        }
      }

      if (!foundGroup) {
        regularFields.push(label);
      }
    });

    // Convert grouped fields into FieldGroup objects
    const groups: FieldGroup[] = [];
    Object.entries(groupMap).forEach(([groupName, fieldMap]) => {
      if (fieldMap.size > 0) {
        const fields = Array.from(fieldMap.entries()).map(([label, type]) => ({
          label,
          type
        }));

        groups.push({
          groupName,
          fields
        });
      }
    });

    return { groups, regularFields };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!country.trim()) {
      alert('Please select a country');
      return;
    }

    // Only require state if the country has states/regions
    if (availableStates.length > 0 && !state.trim()) {
      alert('Please select a state/province/region');
      return;
    }

    setLoading(true);

    try {
      // Fetch the template from the API
      // If country has no states, use country-only endpoint (state will use country name)
      const stateOrCountry = availableStates.length > 0 ? state : country;
      const response = await apiClient.get(`/wills/template/${encodeURIComponent(country)}/${encodeURIComponent(stateOrCountry)}`);

      const template = response.data.content;
      setWillTemplate(template);

      // Parse the template to extract field placeholders
      // Look for patterns like [Field Name], {Field Name}, or Field Name: ___
      const fieldPatterns = [
        /\[([^\]]+)\]/g,           // [Field Name]
        /\{([^}]+)\}/g,            // {Field Name}
        /([A-Z][a-zA-Z\s']+?):\s*_{3,}/g,  // Field Name: ___
      ];

      const fieldSet = new Set<string>();

      for (const pattern of fieldPatterns) {
        let match;
        while ((match = pattern.exec(template)) !== null) {
          const fieldName = match[1].trim();
          // Skip if it contains "signature" or is very short
          if (fieldName.length > 2 && !/signature/i.test(fieldName)) {
            fieldSet.add(fieldName);
          }
        }
      }

      // Convert to array and consolidate fields
      const rawFieldLabels = Array.from(fieldSet);
      const allFieldLabels = consolidateFields(rawFieldLabels);

      // Detect field groups and regular fields
      const { groups, regularFields } = detectFieldGroups(allFieldLabels, template);

      // Create regular field objects
      const fields: WillField[] = regularFields.map(fieldName => {
        // Check if this is a date field
        const isDateField = /\bdate\b/i.test(fieldName) && !/\bdates\b/i.test(fieldName);

        return {
          label: fieldName,
          value: '',
          type: isDateField ? 'date' : 'text'
        };
      });

      // Initialize group instances with one empty instance for each group
      const initialGroupInstances: Record<string, GroupInstance[]> = {};
      groups.forEach(group => {
        const emptyInstance: GroupInstance = {};
        group.fields.forEach(field => {
          emptyInstance[field.label] = field.type === 'date' ? { value: '', displayValue: '' } : '';
        });
        initialGroupInstances[group.groupName] = [emptyInstance];
      });

      setFieldGroups(groups);
      setGroupInstances(initialGroupInstances);
      setWillFields(fields);
      setShowWillFields(true);
    } catch (error) {
      console.error('Error getting will template:', error);
      alert('Failed to get will template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (index: number, value: string) => {
    setWillFields(prev => {
      const updated = [...prev];
      const field = updated[index];

      // For date fields, format the date according to country
      if (field.type === 'date' && value) {
        const formattedDate = formatDateForCountry(value, country);
        updated[index] = {
          ...field,
          value, // Keep ISO format for data storage
          displayValue: formattedDate // Formatted display value
        };
      } else {
        updated[index] = { ...field, value };
      }

      return updated;
    });
  };

  const handleGroupFieldChange = (groupName: string, instanceIndex: number, fieldLabel: string, value: string, fieldType: 'text' | 'date') => {
    setGroupInstances(prev => {
      const updated = { ...prev };
      const instances = [...(updated[groupName] || [])];

      if (fieldType === 'date' && value) {
        const formattedDate = formatDateForCountry(value, country);
        instances[instanceIndex] = {
          ...instances[instanceIndex],
          [fieldLabel]: { value, displayValue: formattedDate }
        };
      } else {
        instances[instanceIndex] = {
          ...instances[instanceIndex],
          [fieldLabel]: value
        };
      }

      updated[groupName] = instances;
      return updated;
    });
  };

  const handleAddGroupInstance = (groupName: string) => {
    setGroupInstances(prev => {
      const updated = { ...prev };
      const group = fieldGroups.find(g => g.groupName === groupName);
      if (!group) return prev;

      const emptyInstance: GroupInstance = {};
      group.fields.forEach(field => {
        emptyInstance[field.label] = field.type === 'date' ? { value: '', displayValue: '' } : '';
      });

      updated[groupName] = [...(updated[groupName] || []), emptyInstance];
      return updated;
    });
  };

  const handleRemoveGroupInstance = (groupName: string, instanceIndex: number) => {
    setGroupInstances(prev => {
      const updated = { ...prev };
      const instances = [...(updated[groupName] || [])];

      // Keep at least one instance
      if (instances.length > 1) {
        instances.splice(instanceIndex, 1);
        updated[groupName] = instances;
      }

      return updated;
    });
  };

  const handleGenerate = async () => {
    setLoading(true);

    try {
      // Use the stored template
      let filledTemplate = willTemplate;

      // Fill in the template with user-provided field values
      willFields.forEach(field => {
        // Get the value to use (formatted date or regular value)
        const value = field.type === 'date' && field.displayValue
          ? field.displayValue
          : field.value;

        // Only replace if the user provided a value
        if (value.trim()) {
          // Escape special regex characters in the field label
          const escapedLabel = field.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

          // Create a regex to find the field name in various formats
          // This will match patterns like: [Field Name], {Field Name}, Field Name: ___
          const fieldPattern = new RegExp(
            `(\\[${escapedLabel}\\]|\\{${escapedLabel}\\}|${escapedLabel}:\\s*_{3,})`,
            'gi'
          );
          filledTemplate = filledTemplate.replace(fieldPattern, value);
        }
      });

      // Fill in group instances (beneficiaries, executors, etc.)
      fieldGroups.forEach(group => {
        const instances = groupInstances[group.groupName] || [];
        if (instances.length === 0) return;

        // Build a formatted list of all instances
        const instanceNames: string[] = [];
        instances.forEach(instance => {
          const nameField = instance['Name'];
          let name = '';
          if (typeof nameField === 'string') {
            name = nameField;
          } else if (nameField && typeof nameField === 'object') {
            name = nameField.displayValue || nameField.value;
          }
          if (name && name.trim()) {
            instanceNames.push(name);
          }
        });

        if (instanceNames.length === 0) return;

        // Create different format variations of the list
        const commaSeparated = instanceNames.join(', ');
        const bulletList = instanceNames.map((name, idx) => `${idx + 1}. ${name}`).join('\n');
        const slashSeparated = instanceNames.join('/');

        const groupNameLower = group.groupName.toLowerCase();
        // Map singular group names to their plural counterparts
        const groupNamePlural = groupNameLower === 'beneficiary'
          ? 'beneficiaries'
          : groupNameLower === 'executor'
          ? 'executors'
          : groupNameLower === 'witness'
          ? 'witnesses'
          : groupNameLower === 'guardian'
          ? 'guardians'
          : groupNameLower + 's'; // Default: just add 's' for seller, buyer, etc.

        // Strategy 1: Replace bracketed placeholders that mention the group
        // Patterns like [spouse/children/other beneficiaries], [Name of Executor], [beneficiaries], etc.
        const bracketPattern = new RegExp(
          `\\[([^\\]]*\\b${groupNameLower}\\b[^\\]]*)\\]|` +
          `\\[([^\\]]*\\b${groupNamePlural}\\b[^\\]]*)\\]`,
          'gi'
        );

        filledTemplate = filledTemplate.replace(bracketPattern, (match) => {
          // If the context suggests a list, use comma-separated
          return commaSeparated;
        });

        // Strategy 2: Find sections with bullet points or lists
        // Look for patterns like "* [description]" or "+ [description]"
        const listItemPattern = new RegExp(
          `(\\*|\\+|-|\\d+\\.)\\s+\\[([^\\]]*\\b${groupNameLower}\\b[^\\]]*)\\]|` +
          `(\\*|\\+|-|\\d+\\.)\\s+\\[([^\\]]*\\b${groupNamePlural}\\b[^\\]]*)\\]`,
          'gi'
        );

        // Replace list items with actual names
        let listItemIndex = 0;
        filledTemplate = filledTemplate.replace(listItemPattern, (match, bullet1, content1, bullet2, content2) => {
          const bullet = bullet1 || bullet2;
          if (listItemIndex < instanceNames.length) {
            const name = instanceNames[listItemIndex];
            listItemIndex++;
            return `${bullet} ${name}`;
          }
          return match;
        });

        // Strategy 3: Replace specific numbered placeholders
        instances.forEach((instance, instanceIndex) => {
          group.fields.forEach(field => {
            const fieldValue = instance[field.label];
            let value = '';

            if (typeof fieldValue === 'string') {
              value = fieldValue;
            } else if (fieldValue && typeof fieldValue === 'object') {
              value = fieldValue.displayValue || fieldValue.value;
            }

            if (value && value.trim()) {
              const instanceNum = instanceIndex + 1;

              // Try multiple pattern variations
              const patterns = [
                `${group.groupName} ${field.label} of ${instanceNum}`,
                `${field.label} of ${group.groupName} ${instanceNum}`,
                `${group.groupName} ${instanceNum} ${field.label}`,
                `${field.label} of ${groupNameLower} ${instanceNum}`,
                `${field.label} of ${group.groupName}`,
              ];

              patterns.forEach(pattern => {
                const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const fieldPattern = new RegExp(
                  `(\\[${escapedPattern}\\]|\\{${escapedPattern}\\}|${escapedPattern}:\\s*_{3,})`,
                  'gi'
                );
                filledTemplate = filledTemplate.replace(fieldPattern, value);
              });
            }
          });
        });

        // Strategy 4: Replace section headers followed by placeholders
        // e.g., "ARTICLE I: BENEFICIARIES\n\n[beneficiary details]"
        const sectionPattern = new RegExp(
          `(${group.groupName}|${groupNamePlural}):?\\s*\\n+\\s*\\[[^\\]]*\\]`,
          'gi'
        );

        filledTemplate = filledTemplate.replace(sectionPattern, (match) => {
          const header = match.split('\n')[0];
          return `${header}\n\n${bulletList}`;
        });
      });

      // Display the filled template
      setGeneratedWill(filledTemplate);
      setShowGeneratedWill(true);

      // Auto-save to activities
      // Extract testator name for the activity title
      let testatorName = 'Unknown';
      const testatorField = willFields.find(
        f => f.label.toLowerCase().includes("testator") ||
             f.label.toLowerCase().includes("your name") ||
             f.label.toLowerCase().includes("full name")
      );
      if (testatorField && testatorField.value) {
        testatorName = testatorField.value;
      }

      // Convert willFields array to object format for storage
      const willFieldsObject: { [key: string]: string | { value: string; displayValue: string } } = {};
      willFields.forEach(field => {
        if (field.type === 'date' && field.displayValue) {
          willFieldsObject[field.label] = { value: field.value, displayValue: field.displayValue };
        } else {
          willFieldsObject[field.label] = field.value;
        }
      });

      // Extract group names for storage
      const groupNames = fieldGroups.map(g => g.groupName);

      const savedActivity = {
        id: currentActivityId || generateUUID(),
        name: `Will: ${testatorName}`,
        uploadedFiles: [],
        selectedFiles: new Set<number>(),
        actionHistory: [],
        savedAt: new Date(),
        activityType: 'will-generation' as const,
        willData: {
          country,
          state,
          municipality,
          willFields: willFieldsObject,
          fieldGroups: groupNames,
          groupInstances,
          generatedWill: filledTemplate,
          willTemplate
        }
      };

      if (currentActivityId) {
        updateActivity(savedActivity);
      } else {
        addActivity(savedActivity);
        setCurrentActivityId(savedActivity.id);
      }

      setLastSavedActivityName(savedActivity.name);
      setIsSaved(true);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error generating will:', error);
      alert('Failed to generate will. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    await exportDocument(format);
  };

  const printWill = () => {
    if (!generatedWill) return;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const locationParts = [country, state, municipality].filter(Boolean);
    const locationText = locationParts.length > 0 ? locationParts.join(', ') : 'Will and Testament';
    const htmlContent = marked(generatedWill, { async: false }) as string;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${locationText} - Will and Testament</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; font-weight: bold; margin-bottom: 8px; margin-top: 24px; }
            h2 { font-size: 20px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; }
            h3 { font-size: 18px; font-weight: 600; margin-top: 16px; margin-bottom: 8px; }
            h4 { font-size: 16px; font-weight: 600; margin-top: 12px; margin-bottom: 8px; }
            p { margin-bottom: 12px; line-height: 1.6; }
            ul, ol { margin-left: 24px; margin-bottom: 12px; }
            li { margin-bottom: 4px; line-height: 1.6; }
            strong { font-weight: 600; color: #111827; }
            em { font-style: italic; }
            code { background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace; }
            pre { background-color: #1f2937; color: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; }
            blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; margin: 12px 0; color: #374151; }
            .header { margin-bottom: 32px; padding-bottom: 16px; border-bottom: 2px solid #d1d5db; }
            .document-title { font-size: 20px; font-weight: bold; color: #111827; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="document-title">Will and Testament</div>
          </div>
          <div class="content">
            ${htmlContent}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportDocument = async (format: 'word' | 'text' | 'pdf') => {
    if (!generatedWill) return;

    try {
      const locationParts = [country, state, municipality].filter(Boolean);
      const title = locationParts.length > 0 ? `${locationParts.join(', ')} - Will and Testament` : 'Will and Testament';
      const extension = format === 'word' ? 'docx' : format === 'pdf' ? 'pdf' : 'txt';
      const filename = `${title}.${extension}`;

      const response = await apiClient.post('/export/document', {
        content: generatedWill,
        title: title,
        format: extension
      }, {
        responseType: 'blob'
      });

      // Create blob from response
      const blob = response.data;

      // Try to use File System Access API (Chrome/Edge) for "Save As" dialog
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: format === 'word' ? 'Word Document' : format === 'pdf' ? 'PDF Document' : 'Text File',
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return; // Success, exit function
        } catch (err: any) {
          // User cancelled or API not available, fall back to standard download
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            // User cancelled the save dialog
            return;
          }
        }
      }

      // Fallback: Standard download (goes to Downloads folder)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export document. Please try again.');
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    // Reset the generated will and return to form state
    setGeneratedWill('');
    setShowGeneratedWill(false);
    setShowDeleteDialog(false);

    // Delete the activity from the store if it exists
    if (currentActivityId) {
      const { deleteActivity } = useActivitiesStore.getState();
      deleteActivity(currentActivityId);
      setCurrentActivityId(null);
    }

    // Clear the activity metadata
    setLastSavedActivityName('');
    setIsSaved(false);
    setHasUnsavedChanges(false);
  };

  return (
    <div className="flex flex-col items-center h-full p-8">
      {/* Header */}
      <div className="mb-8">
        <img
          src="/scales-logo.png"
          alt="Scales of Justice"
          className="h-24 w-24 mx-auto mb-4"
        />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">Will and Testament Generation</h1>
      </div>

      {/* Form */}
      <div className="w-full max-w-3xl space-y-6">
        {/* Location Fields */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-56">
              Country <span className="text-red-500">*</span>:
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              disabled={showWillFields}
              className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
            >
              <option value="">Select a country</option>
              {availableCountries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {availableStates.length > 0 && (
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-56">
                State/Province/Region <span className="text-red-500">*</span>:
              </label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
                disabled={showWillFields}
                className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
              >
                <option value="">Select a state/province/region</option>
                {availableStates.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Message when no templates available for selected country/state */}
          {noTemplatesAvailable && country && state && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-amber-800 dark:text-amber-200 text-sm">
                No will templates are currently available for {state}, {country}. Templates for this jurisdiction will be added in a future update.
              </p>
            </div>
          )}

          {/* Submit Buttons */}
          {!showWillFields && (
            <div className="pt-4 flex gap-4">
              <button
                type="submit"
                disabled={loading || noTemplatesAvailable}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? 'Loading Fields...' : 'Form Entry'}
              </button>
              <button
                type="button"
                disabled={true}
                className="flex-1 px-6 py-3 bg-gray-400 text-white rounded-lg cursor-not-allowed transition-colors font-medium"
              >
                Step-by-step
              </button>
            </div>
          )}
        </form>

        {/* Dynamic Will Fields */}
        {showWillFields && willFields.length > 0 && (
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Will Information</h2>
            {willFields.map((field, index) => (
              <div key={index} className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-56">
                  {field.label}:
                </label>
                {field.type === 'date' ? (
                  <input
                    type="date"
                    value={field.value}
                    onChange={(e) => handleFieldChange(index, e.target.value)}
                    className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <input
                    type="text"
                    value={field.value}
                    onChange={(e) => handleFieldChange(index, e.target.value)}
                    placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                    className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                  />
                )}
              </div>
            ))}

            {/* Field Groups (Beneficiaries, Executors, etc.) */}
            {fieldGroups.map((group, groupIndex) => {
              const instances = groupInstances[group.groupName] || [];
              return (
                <div key={groupIndex} className="space-y-4 border-t border-gray-300 dark:border-gray-600 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{group.groupName}s</h3>

                  {instances.map((instance, instanceIndex) => (
                    <div key={instanceIndex} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 space-y-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {group.groupName} {instanceIndex + 1}
                        </span>
                        {instances.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveGroupInstance(group.groupName, instanceIndex)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      {group.fields.map((field, fieldIndex) => {
                        const fieldValue = instance[field.label];
                        let value = '';

                        if (typeof fieldValue === 'string') {
                          value = fieldValue;
                        } else if (fieldValue && typeof fieldValue === 'object') {
                          value = fieldValue.value;
                        }

                        return (
                          <div key={fieldIndex} className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-40">
                              {field.label}:
                            </label>
                            {field.type === 'date' ? (
                              <input
                                type="date"
                                value={value}
                                onChange={(e) => handleGroupFieldChange(group.groupName, instanceIndex, field.label, e.target.value, field.type)}
                                className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            ) : (
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => handleGroupFieldChange(group.groupName, instanceIndex, field.label, e.target.value, field.type)}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => handleAddGroupInstance(group.groupName)}
                    className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium"
                  >
                    + Add Another {group.groupName}
                  </button>
                </div>
              );
            })}

            {/* Generate Button */}
            <div className="pt-4">
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        )}

        {/* Generated Will Display */}
        {showGeneratedWill && generatedWill && (
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Generated Will</h2>
              <div className="flex items-center gap-3">
                <ExportDropdownMenu onExport={handleExport} onPrint={printWill} showPrint={true} />
                <button
                  onClick={handleDeleteClick}
                  className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Delete this will"
                >
                  <TrashIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <pre className="whitespace-pre-wrap font-sans text-gray-900 dark:text-gray-100 leading-relaxed">
                {generatedWill}
              </pre>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Delete Will
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this will? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteDialog(false)}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
